import { ArrayHelpers, Benchmark, Help, StringHelpers, SystemText } from '@salesperformanceint/e4shared';
import React from 'react';
import { useSelector } from 'react-redux';
import posthog from 'posthog-js';
import { Paging } from '@salesperformanceint/e4shared';
import { e4Format, e4Formats } from 'helpers/dates';

export const MetricsTable = ({ text, metrics, id, reloading, show, hideIcon, onClick, pageSize = 100 }) => {
    const { user } = useSelector(state => state.user);
    const [page, setPage] = React.useState(1);
    const [sorted, setSorted] = React.useState([]);
    const [selected, setSelected] = React.useState(null);
    const crmIcon = `/dist/img/${user.crmIntegration?.replace(' ', '_')?.toLowerCase()}-icon.png`;

    React.useEffect(() => {
        const temp = [...metrics].sort((a, b) => (`${a.type}-${a.sequence}` > `${b.type}-${b.sequence}` ? 1 : -1));
        setSorted(temp);
    }, [metrics]);

    return (
        <>
            <div style={pageSize === 100 ? null : { height: '195px' }}>
                <table className="table table-full table-metrics-card">
                    <thead>
                        <tr>
                            <th width="100%"></th>
                            <th className="no-wrap">{text.metrics.table.myMetrics}</th>
                            {user?.show_Benchmarks && (
                                <th className="no-wrap" colSpan="2">
                                    {text.metrics.table.benchmark}
                                </th>
                            )}
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {ArrayHelpers.getPage(sorted, page, pageSize).map(m => {
                            const icon =
                                m.type === 'CRM' || m.ref === 'CRM' ? crmIcon : `/dist/img/${m.ref?.toLowerCase()}-icon.png`;

                            const integration = user.integrations.find(i => i.ref === m.ref);
                            const lastUpdated = integration?.lastUpdated
                                ? text.metrics.table.lastUpdated(e4Format(integration?.lastUpdated, e4Formats.long))
                                : '';

                            const showSecondBtn =
                                m.format !== 'P' &&
                                m.type !== 'M' &&
                                !m.calc_Days &&
                                !m.calc_Avg &&
                                m.extraFields &&
                                m.extraFields.length > 0;

                            return (
                                <tr
                                    key={`${id}-tab-row-${m.id}-${m.type}`}
                                    className={selected === `${m.id}-${m.type}` ? 'selected' : ''}
                                >
                                    <td className={`flex flex-center ${onClick ? 'pointer' : ''}`}>
                                        {!hideIcon ? (
                                            <img
                                                alt=""
                                                loading="lazy"
                                                src={icon}
                                                style={{ height: '20px' }}
                                                className="mr10"
                                            />
                                        ) : null}
                                        <button
                                            className="btn btn-link"
                                            aria-label={text.metrics.table.showBehaviours(m.name)}
                                            onClick={
                                                onClick
                                                    ? () => {
                                                          setSelected(`${m.id}-${m.type}`);
                                                          onClick(m.id, m.type);
                                                      }
                                                    : null
                                            }
                                        >
                                            <span className="text-name">{m.name}</span>
                                        </button>
                                        {(m.description || lastUpdated) && (
                                            <Help id={`metric-help-${id}-${m.id}`}>
                                                <>
                                                    {m.description}
                                                    {lastUpdated ? (
                                                        <div className={m.description ? 'mt10' : ''}>{lastUpdated}</div>
                                                    ) : null}
                                                </>
                                            </Help>
                                        )}
                                        {m.type === 'RealTime' && (
                                            <Help
                                                id={`realtime-help-${id}-${m.id}`}
                                                target={
                                                    <i
                                                        className="fa fa-clock ml10 text-warning"
                                                        style={{ fontSize: '0.8em' }}
                                                    />
                                                }
                                            >
                                                {text.metrics.table.realtime}
                                            </Help>
                                        )}
                                    </td>
                                    <td>
                                        <div className="pr10">
                                            {reloading ? (
                                                <i className="fa fa-spin fa-cog text-sm" />
                                            ) : (
                                                <b>{StringHelpers.formatMetric(m.format, m.value, user.currencySymbol)}</b>
                                            )}
                                        </div>
                                    </td>
                                    {user?.show_Benchmarks && (
                                        <td>
                                            <div style={{ minWidth: '75px' }}>
                                                <Benchmark percent={reloading ? 0 : m.percentile} style="simple" />
                                            </div>
                                        </td>
                                    )}
                                    {user?.show_Benchmarks && (
                                        <td>
                                            <div className="pr10">
                                                {!reloading ? (
                                                    <div className="ml5 text-benchmark">
                                                        {m.percentile}
                                                        {StringHelpers.getOrdinalSuffix(m.percentile ?? 0)}
                                                    </div>
                                                ) : (
                                                    <span>-</span>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                    <td>
                                        <div className="text-right table-btns no-wrap">
                                            <button
                                                data-testid="button-metric-over-time"
                                                className="btn btn-xs btn-primary"
                                                title={text.metrics.table.overtime}
                                                aria-label={text.metrics.table.overtime}
                                                disabled={m.type === 'RealTime'}
                                                onClick={() => {
                                                    show('metric-line', m);
                                                    posthog.capture('show-metric-over-time', {
                                                        integration: m.type === 'CRM' ? 'CRM' : m.integration,
                                                        metric: m.name,
                                                    });
                                                }}
                                            >
                                                <i className="fa fa-line-chart" />
                                            </button>
                                            <button
                                                data-testid="button-metric-pie"
                                                className={`btn btn-xs btn-primary`}
                                                title={text.metrics.table.breakdown}
                                                aria-label={text.metrics.table.breakdown}
                                                disabled={!m.value || !showSecondBtn}
                                                onClick={() => {
                                                    show('metric-pie', m);
                                                }}
                                            >
                                                <i className="fa fa-pie-chart" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {metrics.length > pageSize && (
                <div className="pauto">
                    <Paging recordCount={metrics.length} page={page} goToPage={setPage} pageSize={pageSize} bullets />
                </div>
            )}
        </>
    );
};

export default MetricsTable;
